import type { PropsWithChildren } from 'react'
import { createContext, useContext } from 'react'

import type { ContentfulRichText } from '../components/ContentfulRichText'
import type { ContentfulImageWithPlaceholder } from '../components/ContentfulImageWithPlaceholder'
import type { BlockQuote } from '../components/ComposeComponentBlockQuote'
import type { DownloadableAsset } from '../components/ComposeComponentDownloadableAsset'
import type { HorizontalImageCTA } from '../components/ComposeComponentHorizontalImageCTA'
import type { ContentfulImage } from '../components/ComposeComponentImage'
import type { ImageAndAdjacentTextList } from '../components/ComposeComponentImageAndAdjacentTextList'
import type { RichTextColouredBackground } from '../components/ComposeComponentRichTextColouredBackground'
import type { ComposeComponentRichTextCTA } from '../components/ComposeComponentRichTextCTA'
import type { YoutubeVideo } from '../components/ComposeComponentYoutubeVideo'

export interface BlockComponents {
  ContentfulRichText: typeof ContentfulRichText
  ContentfulImageWithPlaceholder: typeof ContentfulImageWithPlaceholder
  BlockQuote: typeof BlockQuote
  ContentfulImage: typeof ContentfulImage
  DownloadableAsset: typeof DownloadableAsset
  HorizontalImageCTA: typeof HorizontalImageCTA
  ComposeComponentRichTextCTA: typeof ComposeComponentRichTextCTA
  RichTextColouredBackground: typeof RichTextColouredBackground
  YoutubeVideo: typeof YoutubeVideo
  ImageAndAdjacentTextList: typeof ImageAndAdjacentTextList
}

type InlineComponents = object

export const ContentfulContext = createContext<
  | { blockComponents: BlockComponents; inlineComponents: InlineComponents }
  | undefined
>(undefined)

interface ContentfulProviderProps extends PropsWithChildren {
  blockComponents: BlockComponents
  inlineComponents: InlineComponents
}

export function ContentfulProvider({
  children,
  blockComponents,
  inlineComponents,
}: ContentfulProviderProps) {
  return (
    <ContentfulContext.Provider value={{ blockComponents, inlineComponents }}>
      {children}
    </ContentfulContext.Provider>
  )
}

export function useContentful() {
  const context = useContext(ContentfulContext)

  if (typeof context === 'undefined') {
    throw new Error('Did you forget to set context')
  }

  return context
}
